import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { currentBrand } from "@/lib/brand";
import { isStaffEmail } from "@/lib/auth-domain";
import { useAuth } from "@/hooks/use-auth";

export const Route = createFileRoute("/login")({
  component: LoginPage,
});

function LoginPage() {
  const nav = useNavigate();
  const { profile, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [authLoading, setAuthLoading] = useState(false);

  // Redirect based on role after profile loads
  useEffect(() => {
    if (!loading && profile) {
      if (profile.role === "staff" || profile.role === "admin") {
        nav({ to: "/app/staff/today" });
      } else {
        nav({ to: "/app/customer/dashboard" });
      }
    }
  }, [loading, profile, nav]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setAuthLoading(false);
    if (error) {
      toast.error(error.message);
      // Show hint for staff-domain emails
      if (isStaffEmail(email)) {
        setTimeout(() => {
          toast(
            "This looks like a Resolute staff email. If you're staff, try the staff login instead.",
            {
              action: {
                label: "Staff login",
                onClick: () => nav({ to: "/staff-login" }),
              },
            },
          );
        }, 500);
      }
      return;
    }
    // Check if MFA challenge is required
    const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    if (aal?.nextLevel === "aal2" && aal.nextLevel !== aal.currentLevel) {
      nav({ to: "/login/mfa" });
      return;
    }
    toast.success("Welcome back!");
    // Redirect will be handled by useEffect when profile loads
  };

  return (
    <div className="grid min-h-screen place-items-center bg-gradient-soft p-6">
      <div className="w-full max-w-md rounded-3xl border border-border bg-card p-8 shadow-soft">
        <div className="mb-6 flex items-center gap-3">
          <div className="grid h-16 w-16 shrink-0 place-items-center rounded-2xl bg-gradient-hero p-3 shadow-soft">
            <img
              src={currentBrand.logoSrc}
              alt={`${currentBrand.name} logo`}
              className="h-full w-auto"
            />
          </div>
          <div>
            <h1 className="text-xl font-bold">Sign in to FlightDeck</h1>
            <p className="text-sm text-muted-foreground">
              Your trade print portal for Resolute and CraftyJet customers
            </p>
          </div>
        </div>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Email</Label>
            <Input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded-2xl"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Password</Label>
            <Input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="rounded-2xl"
            />
          </div>
          <Button
            type="submit"
            disabled={authLoading}
            className="w-full rounded-full bg-gradient-hero text-primary-foreground shadow-soft hover:opacity-90"
          >
            {authLoading ? "Signing in…" : "Sign in"}
          </Button>
        </form>
        <p className="mt-4 text-center text-xs text-muted-foreground">
          Staff member?{" "}
          <Link to="/staff-login" className="font-semibold text-primary">
            Use the staff login
          </Link>
          .
        </p>
        <p className="mt-5 text-center text-sm text-muted-foreground">
          No account?{" "}
          <Link to="/signup" className="font-semibold text-primary">
            Create one
          </Link>
        </p>
      </div>
    </div>
  );
}
